import {
  expirationMdYFromEffAndIlf,
  formatIlfLimitShort,
  getDefaultIlfDlfRow,
  policyTypeFromIlf,
  premiumOnlyFromOrderRatingResponse,
  softQuotePremiumFromIlf,
  totalOnlyFromOrderRatingResponse,
} from "@/modules/Quote/utils/ilfHelpers.js";
import { fmtDate } from "@/shared/utils/dateHelpers.js";
import { formatSubmissionListDate } from "@/shared/utils/format.js";
import { parsePositiveSubmissionId } from "@/modules/Quote/utils/submission.js";
import { isQuestionVisible } from "@/modules/Quote/api/questionsApi.js";

/**
 * Pure derivation of every "read-only" value exposed by QuoteContext.
 *
 * Takes the raw state inputs (form fields, API responses, step, etc.) and
 * returns the snapshot / quote-strip / review / payment / bound values that
 * pages read. No React, no setters, no side effects — easy to reason about
 * and easy to test in isolation.
 *
 * To add a new derived value: compute it from the inputs and add it to the
 * returned object. To pull a new input: add it to the destructured arg list
 * AND to the inputs passed in QuoteProvider.
 */
export function computeDerivedValues({
  // form / step
  step, zip, effDate, pa2Subs,
  firstName, lastName,
  regEmail, regPhone,
  practiceAddress1, practiceAddress2, practiceCity, practiceState, practiceZip,
  // API responses + loading flags
  ilfDlfResponse,
  paymentOrderDetails, paymentOrderLoading,
  calculateRatingResult, calculateRatingLoading,
  flowSubmissionId,
  submissionQuestionGroups,
  underwritingAnswers,
  hiddenQuestionIds,
  boundPolicyNumberFromInvoice,
  boundTotalAmount,
  insuredProfile,
}) {
  // ── State + class ────────────────────────────────────────────────────
  // Practice state comes entirely from the ILF/DLF API response now
  // (ilfDlfResponse.st / .state) — no client-side ZIP→state guessing.
  // If the API hasn't returned yet, `state` is empty and downstream code
  // shows neutral placeholders ("Your state", etc.).
  const state = (ilfDlfResponse?.st ?? ilfDlfResponse?.state ?? "")
    .toString().trim().toUpperCase().slice(0, 2);
  const actualClass = pa2Subs.length > 0 ? "PA2" : "PA1";

  // ── Premium / total intermediates ────────────────────────────────────
  const ilfDefaultRow = getDefaultIlfDlfRow(ilfDlfResponse);
  const softQuotePremiumApi = softQuotePremiumFromIlf(ilfDlfResponse);
  const ratingR = paymentOrderDetails?.ratingResponse;
  const orderRatedPremium = premiumOnlyFromOrderRatingResponse(ratingR);
  const orderRatedTotal = totalOnlyFromOrderRatingResponse(ratingR);
  const orderRatedStripPremium = orderRatedTotal ?? orderRatedPremium ?? null;
  const premium = orderRatedPremium ?? softQuotePremiumApi ?? 0;
  const snapshotPremium = softQuotePremiumApi ?? orderRatedPremium ?? 0;

  const calculateRatedPremium =
    calculateRatingResult?.premium != null && Number.isFinite(Number(calculateRatingResult.premium))
      ? Math.round(Number(calculateRatingResult.premium))
      : null;
  // On the License/Scope step (3) the user can edit ZIP and the freshly
  // re-quoted ilfDlfResponse is the authoritative live estimate — order
  // rating is stale until the next save. From step 4 onward the ratingR
  // is finalized (includes tax/fees) so it wins back.
  const preferIlfForLiveQuote = step === 3 && ilfDlfResponse?.premium != null;
  // Preserve the decimal — backend ships values like 1685.47006775 and the
  // QuoteStrip renders via `.toLocaleString()` (default 0–3 fraction digits)
  // so users see e.g. "1,685.47" rather than a rounded "1,685".
  // QuoteFormData ships BOTH numbers per swagger:
  //   - `premium` → base premium (rail "Est. premium")
  //   - `total`   → premium + tax + fees (QuoteStrip "Your quote")
  const ilfPremiumRaw = (() => {
    const raw = ilfDlfResponse?.premium;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  const ilfTotalRaw = (() => {
    const raw = ilfDlfResponse?.total;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const snapshotBasePremium = (() => {
    if (calculateRatedPremium != null) return calculateRatedPremium;
    if (preferIlfForLiveQuote && ilfPremiumRaw != null) return ilfPremiumRaw;
    if (orderRatedPremium != null) return orderRatedPremium;
    if (ilfPremiumRaw != null) return ilfPremiumRaw;
    return 0;
  })();

  // ── Quote strip ──────────────────────────────────────────────────────
  const quoteStripPremium = orderRatedStripPremium ?? snapshotPremium;
  const quoteStripAmountPending =
    calculateRatingLoading
    || (
      Boolean(flowSubmissionId)
      && step >= 3
      && step <= 7
      && paymentOrderLoading
      && (
        step >= 4
          ? orderRatedTotal == null
          : (softQuotePremiumApi == null && orderRatedPremium == null && orderRatedTotal == null)
      )
    );

  // ── Snapshot block (state/zip/limits/policy type) ────────────────────
  const snapshotState = (() => {
    const label =
      ilfDlfResponse?.st
      ?? ilfDefaultRow?.st
      ?? ilfDlfResponse?.state
      ?? ilfDefaultRow?.state
      ?? ratingR?.practiceLocation
      ?? ratingR?.state
      ?? state;
    return label != null && String(label).trim() !== "" ? String(label).trim() : (state || "Your state");
  })();
  // Practice ZIP being edited on step 3 wins over the saved order's zip.
  const snapshotZip = (
    (step === 3 && practiceZip)
    || ratingR?.practicezipcode
    || practiceZip
    || zip
    || ""
  ).toString().trim();
  const snapshotLimits = ilfDlfResponse?.defaultIlfDlfName
    || formatIlfLimitShort(ilfDefaultRow)
    || ratingR?.coverageLimitTitle
    || "—";
  const snapshotPolicyType = (() => {
    if (ilfDefaultRow) return policyTypeFromIlf(ilfDefaultRow);
    const raw = ratingR?.currentprior;
    if (raw != null && String(raw).toLowerCase().includes("occ")) return "Occurrence";
    return "Claims-made";
  })();
  const snapshotDefense = (() => {
    const d = ilfDlfResponse?.defaultDefense ?? ratingR?.defense;
    return d != null && String(d).trim() !== "" ? String(d).trim() : "";
  })();
  const snapshotPolicyLine = snapshotDefense ? `${snapshotPolicyType} · ${snapshotDefense}` : snapshotPolicyType;
  const snapshotClaimsShort = snapshotPolicyType;
  const snapshotExpMdY = expirationMdYFromEffAndIlf(effDate, ilfDlfResponse);

  // ── Total + annual ───────────────────────────────────────────────────
  const total = orderRatedTotal ?? softQuotePremiumApi ?? 0;
  const calculateRatedTotal =
    calculateRatingResult?.total != null && Number.isFinite(Number(calculateRatingResult.total))
      ? Math.round(Number(calculateRatingResult.total))
      : null;
  const quoteStripAnnualTotal = calculateRatedTotal
    ?? (preferIlfForLiveQuote ? (ilfTotalRaw ?? ilfPremiumRaw) : null)
    ?? orderRatedTotal
    ?? orderRatedPremium
    ?? softQuotePremiumApi
    ?? 0;

  // ── Underwriting gate (any-yes / all-answered) ───────────────────────
  const underwritingGroupForGate = submissionQuestionGroups.find(
    (g) => /underwriting/i.test(String(g?.groupName || "")),
  );
  const underwritingGateQuestions = (underwritingGroupForGate?.questions || []).filter(
    (q) => (q?.questionType === "YES_NO" || q?.questionType === "RADIO_BUTTON")
      && isQuestionVisible(q, hiddenQuestionIds),
  );
  const anyYes = underwritingGateQuestions.some((q) => {
    const sel = underwritingAnswers[String(q.id)];
    if (sel == null) return false;
    const opt = (q.options || []).find((o) => String(o.id) === String(sel));
    return opt && /^yes/i.test(String(opt.optionValue || opt.optionLabel || ""));
  });
  const allAnswered = underwritingGateQuestions.length > 0
    && underwritingGateQuestions.every((q) => underwritingAnswers[String(q.id)] != null);
  const isReferral = anyYes || actualClass === "PA2";

  // ── Review page ──────────────────────────────────────────────────────
  const reviewR = ratingR;
  const reviewOd = paymentOrderDetails;
  const reviewC = reviewOd?.contactResponse;
  const reviewLoc = reviewOd?.locationResponse;
  const reviewTotalFromApi =
    reviewR?.total != null && Number.isFinite(Number(reviewR.total))
      ? Math.round(Number(reviewR.total))
      : null;
  const reviewDisplayTotal = reviewTotalFromApi != null ? reviewTotalFromApi : total;
  const paymentAmountDue = (() => {
    const apiTotal = Number(reviewR?.total);
    if (Number.isFinite(apiTotal) && apiTotal > 0) return Math.round(apiTotal * 100) / 100;
    const fallback = Number(total);
    return Number.isFinite(fallback) && fallback > 0 ? Math.round(fallback * 100) / 100 : 0;
  })();
  const reviewApiPolicyholder = reviewOd
    && (reviewOd.companyname || [reviewOd.insuredfirstname, reviewOd.insuredlastname].filter(Boolean).join(" "));
  const reviewApplicantName = reviewApiPolicyholder
    ? (reviewOd.companyname ? reviewApiPolicyholder : `${reviewApiPolicyholder}, PA-C`)
    : `${firstName} ${lastName}, PA-C`;
  const reviewApplicantEmail = reviewC?.email || regEmail || "—";
  const reviewApplicantPhone = reviewC?.contactnumber || regPhone || "—";
  const reviewApplicantAddr = reviewLoc
    ? [reviewLoc.address1, reviewLoc.city, reviewLoc.state, reviewLoc.zipcode]
        .filter((x) => x != null && String(x).trim() !== "").join(", ")
    : [practiceAddress1, practiceAddress2, practiceCity, practiceState, practiceZip]
        .filter((x) => x != null && String(x).trim() !== "").join(", ");
  const paymentBillingDetails = {
    name: reviewApplicantName && reviewApplicantName !== "—" ? reviewApplicantName : undefined,
    email: reviewApplicantEmail && reviewApplicantEmail !== "—" ? reviewApplicantEmail : undefined,
    phone: reviewApplicantPhone && reviewApplicantPhone !== "—" ? reviewApplicantPhone : undefined,
  };
  const reviewHasApiBreakdown = reviewR != null
    && (reviewR.premium != null || reviewR.tax != null || reviewR.fees != null || reviewR.total != null);
  const reviewPolicyPeriod = reviewR?.effectiveDate && reviewR?.expirationDate
    ? `${formatSubmissionListDate(reviewR.effectiveDate)} – ${formatSubmissionListDate(reviewR.expirationDate)}`
    : `${fmtDate(effDate)} – ${snapshotExpMdY}`;
  const reviewLimitsLabel = reviewR?.coverageLimitTitle || snapshotLimits;
  const reviewStateLabel =
    (reviewR?.practiceLocation && String(reviewR.practiceLocation).trim())
    || (reviewLoc?.state && String(reviewLoc.state).trim())
    || snapshotState;

  // ── Bound confirmation ───────────────────────────────────────────────
  const boundOrderNumberDisplay = (() => {
    if (boundPolicyNumberFromInvoice != null && String(boundPolicyNumberFromInvoice).trim() !== "") {
      return String(boundPolicyNumberFromInvoice).trim();
    }
    const od = paymentOrderDetails;
    if (od && typeof od === "object") {
      const pn = od.policynumber ?? od.policyNumber;
      if (pn != null && String(pn).trim() !== "") return String(pn).trim();
      const qn = od.quotenumber ?? od.quoteNumber;
      if (qn != null && String(qn).trim() !== "") return String(qn).trim();
      const sidOd = parsePositiveSubmissionId(od.submissionId ?? od.submissionid);
      if (sidOd != null) return `#${sidOd}`;
    }
    const sid = parsePositiveSubmissionId(flowSubmissionId);
    return sid != null ? `#${sid}` : "—";
  })();
  const boundConfirmationAmount = boundTotalAmount != null ? boundTotalAmount : reviewDisplayTotal;
  const boundConfirmationAmountRounded = (() => {
    const n = Number(boundConfirmationAmount);
    return Number.isFinite(n) ? Math.round(n) : Math.round(Number(total));
  })();
  const boundSummaryLine = [snapshotLimits, snapshotPolicyType, reviewStateLabel]
    .filter((x) => x != null && String(x).trim() !== "")
    .join(" · ");

  // ── Header / phase chip ──────────────────────────────────────────────
  const headerUserInitials = (insuredProfile?.abbreviation || insuredProfile?.username || "JD")
    .toString().slice(0, 2).toUpperCase();
  const phaseLabel = step <= 1 ? "Soft quote" : step === 2 ? "Registration" : "Full application";

  return {
    // state / class
    state, actualClass,
    // premium / total
    premium, total,
    // snapshot
    snapshotPremium, snapshotBasePremium, snapshotState, snapshotZip, snapshotLimits,
    snapshotPolicyType, snapshotPolicyLine, snapshotClaimsShort, snapshotExpMdY,
    // quote strip
    quoteStripPremium, quoteStripAnnualTotal, quoteStripAmountPending,
    // underwriting gate
    anyYes, allAnswered, isReferral,
    // review
    reviewR, reviewDisplayTotal, reviewApplicantName, reviewApplicantEmail,
    reviewApplicantPhone, reviewApplicantAddr, reviewHasApiBreakdown,
    reviewPolicyPeriod, reviewLimitsLabel, reviewStateLabel,
    paymentAmountDue, paymentBillingDetails,
    // bound confirmation
    boundOrderNumberDisplay, boundConfirmationAmountRounded, boundSummaryLine,
    // header
    headerUserInitials, phaseLabel,
  };
}
